import {Component, Prop, h, State, Element, Event, EventEmitter} from '@stencil/core';

import {
  loopInitiMethods,
  renderCarouselItems,
  scrollBarBehaviourOnInput,
  scrollBarBehaviourOnChange,
  getLiWidth,
  setLeftPaddleAriaLabel,
  setRightPaddleAriaLabel,
  showNavButtons,
  hideNavButtons,
  handleLeftClick,
  handleRightClick,
  setInitialAccessibility,
  handleAccessibility,
  handleKeyPresses,
  setSnapValues,
  handleLoopOver,
  handleAutoScrollEnd,
  handleListFocus,
  renderDesktopItems
} from './carousel-utils';

import { handleSwipe, handleSwipeCancel, handleSwipeEnd, handleSwipeStart } from './swipe-utils';
import { ga4GenericClickEvent } from "@utils/offer-utils";
import store from "@utils/store";

@Component({
  tag: 'vs-carousel-container',
  styleUrl: './_vs-carousel-container.scss',
})
export class CarouselContainer {
  @Element() private element: HTMLVsCarouselContainerElement;

  @Prop() data: String | any;
  @Prop() datapath: string;
  @Prop() dataid: string;
  @State() typicalSlideWidth = 0;
  @State() docDir = 'ltr';
  docDirFactor = 1;
  @State() leftPaddleAriaLabel: string;
  @State() rightPaddleAriaLabel: string;
  @State() screenWidth = 0;
  @State() scrollVals = [];
  @State() carouselWidth = 0;
  @State() scrollableWidth = 0;
  @State() loopSwiping = false;
  @State() scrollTimer = null;
  @State() swiped = 0;
  @State() isTransitionStarted = false;
  @State() isClonedNext = false;
  @State() isClonedPrev = false;
  @State() autoPlay = false;
  @State() autoScrollSpeed = 1;
  @State() isLoading = true;
  @State() statusMessage: string;
  @State() loading: boolean = true;
  @State() error: any = '';
  @State() firstLoad: boolean = false;
  startAutoScollAriaLabel: string = 'Start Auto Scroll';
  stopAutoScollAriaLabel: string = 'Stop Auto Scroll';
  tabPaused: boolean = false;
  autoScrollDirection = 'left';
  autoScroll = false;
  decelerateAutoScroll = false;
  decelerateSpeed = 0.4;
  isSwiping = false;
  scrollState = 0;
  translateVal = 0;
  paddlePressed = false;
  currentIndex = 0;
  childType: string;
  displayCBundleDate = false;
  carouselName: string;
  layout: string;
  loop: boolean = false;
  hideScroll: boolean = false;
  carouselData: object;
  numItems: 0;
  layoutFactor = 0.5;
  numLoopItems = 0;
  eachSideClone = 4;
  scrollRange: HTMLInputElement;
  nextButton: HTMLElement;
  prevButton: HTMLElement;
  allListItems: HTMLCollectionOf<HTMLLIElement>;
  ctas: HTMLCollectionOf<HTMLAnchorElement>;
  headerIds: [];
  childItems: any[];
  carouselList: HTMLElement;
  backgroundColor: string;
  carouselWrapper: HTMLElement;
  initialSwipeVal: number;
  path: string;
  @Prop() mode: string;
  @Event() dataRequest: EventEmitter<string>;
  async componentWillLoad() {
    this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    this.path = this.datapath;
    if (this.mode === 'authoring') {
      // Emit an event to request data from parent vs-app-root
      this.dataRequest.emit();
    }
    store.subscribe(() => {
      const state = store.getState().data;
      this.loading = state.loading;
      this.error = state.error;
      if (state.data) {
        this.carouselData = state.data.page[this.dataid];
        this.docDir = document.dir || 'ltr';
        if (this.carouselData) {
          this.processCarouselData();
        }
      }
      this.isLoading=false;
      this.firstLoad=true;
    });
  }

  private processCarouselData() {
    this.autoScroll = this.carouselData['carouselMode'] === 'autoscroll';
    this.autoScrollDirection = this.carouselData['autoScrollDirection'];
    if (this.docDir == 'rtl') {
      this.autoScrollDirection = this.autoScrollDirection == 'left' ? 'right' : 'left';
    }
    this.decelerateAutoScroll = this.carouselData['autoScrollDeceleration'] === 'yes';
    this.startAutoScollAriaLabel = this.carouselData['startAutoScollAriaLabel'] || 'Start Auto Scroll';
    this.stopAutoScollAriaLabel = this.carouselData['stopAutoScollAriaLabel'] || 'Stop Auto Scroll';
    this.carouselName = this.carouselData['carouselName'];
    this.backgroundColor = this.carouselData['backgroundColor'];
    window.addEventListener('resize', this.resizeEventHandler);
    this.childType = this.carouselData['componentType'];
    this.loop = this.carouselData['loop'] === 'yes' || this.carouselData['carouselMode']?.length > 0;
    this.hideScroll = this.loop;
    this.numItems = this.carouselData['child'].length;
    this.layout = this.carouselData['layout'];
    this.layoutFactor = this.layout === '2up' ? 1 / 2 : 1 / 3;
    this.typicalSlideWidth = getLiWidth(this);
    if (this.childType === 'CBundle') {
      this.displayCBundleDate = this.carouselData['displayPublishDate'];
    }
    if (this.childType === 'OfferBundle') {
      this.hideScroll = true;
    }
    if (this.loop) {
      loopInitiMethods(this);
    }
    this.carouselWidth = this.setCarouselListWidth();

    setSnapValues(this);
    this.docDirFactor = document.dir === 'rtl' ? -1 : 1;
    this.leftPaddleAriaLabel = setLeftPaddleAriaLabel(this);
    this.rightPaddleAriaLabel = setRightPaddleAriaLabel(this);
    this.childItems = renderCarouselItems(this);
    this.scrollState = this.scrollVals[this.currentIndex];
    this.swiped = this.scrollState;
    this.initialSwipeVal = Math.abs(this.scrollState);
  }

  componentDidUpdate() {
    if (this.firstLoad && !this.isLoading && this.carouselData) {
      this.element.addEventListener('focusout', this.handleFocusOut);
      this.carouselList = this.element.querySelector<HTMLElement>('ul');
      this.scrollRange = this.element.querySelector<HTMLInputElement>('.vs-carousel-scroll');
       let nextButtonCheck = `[aria-label="${this.carouselData['ariaLabelNextSlide']} ${this.carouselName}"]`;
       let prevButtonCheck = `[aria-label="${this.carouselData['ariaLabelPreviousSlide']} ${this.carouselName}"]`;
       this.nextButton = this.element.querySelector<HTMLElement>(nextButtonCheck);
       this.prevButton = this.element.querySelector<HTMLElement>(prevButtonCheck);

      const carouselContent = this.element.querySelector('.vs-carousel-container-content');
      if (carouselContent) {
        this.allListItems = carouselContent.children as HTMLCollectionOf<HTMLLIElement>;
      }

      handleAccessibility(this);
      if ((this.screenWidth < 992) && ((this.autoScroll && !this.autoPlay) || !this.autoScroll)) {
        this.showNavButtons();
      }

      if (!this.loop) {
        this.setThumbWidth();
      }
      setInitialAccessibility(this);
      this.carouselList.style.setProperty('--carousel-container-width', `${this.carouselWidth}px`);
      if (this.autoScroll && !this.autoPlay) {
        this.toggleAutoScroll();
      }
      /* START GENAI@GHCOPILOT */
      const ctas = this.element.querySelectorAll('.vs-bundle-cta-item a');
      ctas.forEach(cta => {
        cta.addEventListener('focus', () => {
          // Find the parent <li> element with role="listitem"
          const parentListItem = cta.closest('li[role="listitem"]');
          if (parentListItem) {
            // Remove the vs-tab-focused class
            parentListItem.classList.remove('vs-tab-focused');
          }

          // Remove vs-tab-focused from all siblings
          const siblings = parentListItem?.parentElement?.children;
          if (siblings) {
            Array.from(siblings).forEach(sibling => {
              sibling.classList.remove('vs-tab-focused');
            });
          }

          handleListFocus(this); // Retain existing focus handling logic
        });
      });
      this.firstLoad=false;
    }
  }

  /* START GENAI@GHCOPILOT */
  handleFocusOut = () => {
    setTimeout(() => {
      if (!this.element.contains(document.activeElement) && this.tabPaused) {
        this.toggleAutoScroll();
        this.tabPaused = false;
      }
    });
  }
  /* END GENAI@GHCOPILOT */

  private handleTransitionStart(e: any) {
    /* START GENAI@GHCOPILOT */
    if (e.target.classList.contains('vs-carousel-transition')) {
      this.isTransitionStarted = true;
    }
    /* END GENAI@GHCOPILOT */
  }

  private handleTransitionEnd = (e: any) => {
    /* START GENAI@GHCOPILOT */

    if (e.target.classList.contains('vs-carousel-transition')) {

      /* END GENAI@GHCOPILOT */
      this.isTransitionStarted = false;
      if (this.isClonedNext || this.isClonedPrev) {
        handleLoopOver(this);
        this.isClonedNext = false;
        this.isClonedPrev = false;
      }
    }
    if (this.childType === 'OfferBundle') {
      showNavButtons(this);
    }
  };

  setThumbWidth = () => {
    this.scrollRange.style.setProperty('--carousel-thumb-width', `${(this.screenWidth / this.numItems) * 1.4}px`);
  };

  setCarouselListWidth = () => {
    let netHorizontalPadding = this.screenWidth < 1200 ? 0 : this.screenWidth - 1140;
    return this.loop ? this.numLoopItems * this.typicalSlideWidth + netHorizontalPadding : this.numItems * this.typicalSlideWidth + netHorizontalPadding;
  };

  handleKeyPresses = event => {
    handleKeyPresses(event, this);
  };

  showNavButtons = () => {
    if (!this.autoScroll) {
      showNavButtons(this);
    } else if (this.autoScroll) {
      if (!this.autoPlay) {
        showNavButtons(this);
      }
    }
  };

  hideNavButtons = () => {
    this.isSwiping = false;
    if (this.screenWidth >= 992) {
      hideNavButtons(this);
    }
  };

  calcBarVal = () => {
    return Math.abs(this.scrollState) * 1.2;
  };

  resizeEventHandler = () => {
    this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    this.typicalSlideWidth = getLiWidth(this);
    this.carouselWidth = this.setCarouselListWidth();
    if (this.loop) {
      loopInitiMethods(this);
    }
    this.carouselWidth = this.setCarouselListWidth();

    setSnapValues(this);

    if (this.screenWidth < 992) {
      this.showNavButtons();
    } else {
      this.hideNavButtons();
    }
  };

  handleRightPaddleClick = () => {
    handleRightClick(this);
    if (this.childType === 'OfferBundle') {
      ga4GenericClickEvent(this.carouselData['carouselGa4Event'], 'carousel_next');
    }
  };

  handleLeftPaddleClick = () => {
    handleLeftClick(this);
    if (this.childType === 'OfferBundle') {
      ga4GenericClickEvent(this.carouselData['carouselGa4Event'], 'carousel_prev');
    }
  };
  /* START GENAI@GHCOPILOT */
  calculateSwiped = (scrollDirection, scrollSpeed) => {
    return this.docDir === 'rtl'
      ? this.swiped + scrollDirection * scrollSpeed
      : this.swiped - scrollDirection * scrollSpeed;
  };

  resetSwiped = (direction) => {
    if (this.docDir === 'rtl') {
      return direction === 'left' ? this.initialSwipeVal : Math.abs(this.typicalSlideWidth*this.numItems) + this.initialSwipeVal;
    } else {
      return direction === 'left' ? -this.initialSwipeVal : -(Math.abs(this.typicalSlideWidth*this.numItems) + this.initialSwipeVal);
    }
  };

  animate = () => {
    if (!this.autoPlay) {
      cancelAnimationFrame(this.scrollTimer);
      return;
    }

    const scrollDirection = this.autoScrollDirection === 'left' ? 1 : -1;
    this.swiped = this.calculateSwiped(scrollDirection, this.autoScrollSpeed);
    if ((this.autoScrollDirection === 'left' && Math.abs(this.swiped) >= Math.abs(this.typicalSlideWidth*this.numItems) + this.initialSwipeVal) ||
      (this.autoScrollDirection === 'right' && Math.abs(this.swiped) <= Math.abs(this.initialSwipeVal))) {
      this.carouselList.style.transition = `none`;
      this.swiped = this.resetSwiped(this.autoScrollDirection);
    }

    this.scrollTimer = requestAnimationFrame(this.animate);
  };

  toggleAutoScroll = () => {
    this.autoPlay = !this.autoPlay;
    if (this.carouselList) {
      this.carouselList.style.transition = `none`;

      if (this.autoPlay) {
        this.scrollTimer = requestAnimationFrame(this.animate);
        if (this.screenWidth < 992) {
          hideNavButtons(this);
        }
      } else {
        this.showNavButtons();
        cancelAnimationFrame(this.scrollTimer);
        this.carouselList.style.transition = `transform 0.5s ease-in-out`;
        handleAutoScrollEnd(this);
      }
    }
  };

  pauseOnTab = (event: KeyboardEvent) => {
    if (event.key.toLowerCase() === 'tab') {
      if (this.autoPlay && !this.tabPaused) {
        this.tabPaused = true;
        this.toggleAutoScroll();
      }
      const target = event.target as HTMLElement;
      target.classList.remove('vs-tab-focused');
    }
  }

  handleAutoScrollFocus = (event) => {
      const target = event.target as HTMLElement;
      target.classList.add('vs-tab-focused');
  }

  handleAutoScrollBlur = (event) => {
    const target = event.target as HTMLElement;
    target.classList.remove('vs-tab-focused');
  }
  /* END GENAI@GHCOPILOT */

  private getDisplayMode() {
    const showDesktopView = this.screenWidth >= 1200 &&
      ((this.layout === '2up' && this.carouselData['child'].length === 2) ||
       (this.layout === '3up' && this.carouselData['child'].length === 3));

    if (showDesktopView) {
      return 'desktop-2-3-up';
    } else if (this.numItems > 1) {
      return 'carousel';
  }
}
  renderSkeleton() {
    return (
      <div class="vs-carousel-skeleton">
        <div class="vs-carousel-skeleton-wrapper">
          {[1, 2, 3].map(() => (
            <div class="vs-carousel-skeleton-item">
              <div class="vs-carousel-skeleton-image"></div>
              <div class="vs-carousel-skeleton-text">
                <div class="vs-carousel-skeleton-title"></div>
                <div class="vs-carousel-skeleton-description"></div>
              </div>
            </div>
          ))}
        </div>
        <div class="vs-carousel-skeleton-nav">
          <div class="vs-carousel-skeleton-button"></div>
          <div class="vs-carousel-skeleton-button"></div>
        </div>
      </div>
    );
  }
  render() {
    if (this.isLoading) {
      return this.renderSkeleton();
    }
    const displayMode = this.getDisplayMode();

    if (displayMode === 'desktop-2-3-up') {
      return renderDesktopItems(this);
    } else if (displayMode === 'carousel') {
      return (
        <div class={this.backgroundColor + ' vs-pb-3 vs-container-fluid vs-px-0 vs-position-relative'}
             onMouseOver={this.showNavButtons}
             onMouseLeave={this.hideNavButtons}>
          {this.autoScroll ? (
            <div class="vs-container-fluid vs-d-flex vs-justify-content-end vs-toggle-autoscroll">
              <button
                aria-label={this.autoPlay ? this.carouselName + ":" + this.stopAutoScollAriaLabel : this.carouselName + ":" + this.startAutoScollAriaLabel}
                class={this.autoPlay ? 'vs-carousel-media-button vs-carousel-pause-button' : 'vs-carousel-media-button vs-carousel-play-button'}
                data-role="button"
                onClick={() => {
                  this.tabPaused = false;
                  this.toggleAutoScroll()
                }}
                onKeyDown={event => this.pauseOnTab(event)}
                onFocus={event => this.handleAutoScrollFocus(event)}
                onBlur={event => this.handleAutoScrollBlur(event)}>
              </button>
            </div>
          ) : (
            ''
          )}
        <div
          class={this.autoScroll ? 'vs-carousel-container vs-pb-btn' : 'vs-carousel-container'}
          data-loop={this.carouselData['loop']}
          onKeyUp={this.handleKeyPresses}
          key={this.carouselName}
        >
          <div
            class="vs-carousel-container-wrapper vs-carousel-transition"
            onTouchMove={event => handleSwipe(event, this)}
            onMouseMove={event => handleSwipe(event, this)}
            onTouchCancel={event => handleSwipeCancel(event, this)}
            onTouchEnd={event => handleSwipeEnd(event, this)}
            onMouseUp={event => handleSwipeEnd(event, this)}
            onMouseDown={event => handleSwipeStart(event, this)}
            onTouchStart={event => handleSwipeStart(event, this)}
            onMouseLeave={event => handleSwipeEnd(event, this)}
            role='group'
            aria-label={this.carouselName}
          >
            <ul
              style={{ transform: `translate3d(${this.swiped}px,0px,0px)` }}
              class="vs-carousel-container-content vs-carousel-transition"
              role="list"
              onTransitionEnd={this.handleTransitionEnd}
              onTransitionStart={this.handleTransitionStart}
              aria-roledescription="carousel"
            >
              {this.childItems}
            </ul>
          </div>
        </div>
        <div class="vs-carousel-nav">
            <button
              tabindex="-1"
              data-role="button"
              aria-label={`${this.leftPaddleAriaLabel} ${this.carouselName}`}
              class={this.autoScroll ? 'vs-carousel-container-btn vs-autoplay-nav vs-lt-btn vs-d-none' : 'vs-carousel-container-btn vs-lt-btn vs-d-none'}
              onClick={this.handleLeftPaddleClick}
            ></button>

            <button
              tabindex="-1"
              data-role="button"
              aria-label={`${this.rightPaddleAriaLabel} ${this.carouselName}`}
              class={this.autoScroll ? 'vs-carousel-container-btn vs-autoplay-nav vs-rt-btn vs-d-none' : 'vs-carousel-container-btn vs-rt-btn vs-d-none'}
              onClick={this.handleRightPaddleClick}
            ></button>
          </div>
        {this.hideScroll ? (
          ''
        ) : (
          <div class="vs-container-xl">
            <input
              type="range"
              min="0"
              max={this.scrollableWidth}
              value={this.calcBarVal()}
              class="vs-carousel-scroll"
              onInput={event => scrollBarBehaviourOnInput(event, this)}
              onChange={event => scrollBarBehaviourOnChange(event, this)}
              tabindex="-1"
              aria-hidden="true"
            ></input>
          </div>
        )}
      </div>
      );
    }
  }
}
